import { addHours, set } from 'date-fns';
import { TFunction } from 'i18next';
import { formatPhoneNumber } from 'react-phone-number-input';
import { formatDuration, getDurationHours } from 'src/listing/helper';
import { isDateValid } from 'src/common/utils/dateHelpers';
import { MinimalRecentInquiry } from 'src/inquiry/types';
import { AutocompleteOption } from 'src/common/types';
import { Amounts } from 'src/common/types/common/Amounts';
import { InstabookTripTime } from 'src/common/types/api/v4/instabook';
import type { AISummary, Breadcrumb, FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import {
  BOOK_NOW_PICK_UP_TIME_FORMAT, formatDate, todayDate, type TimeFormat,
} from 'src/common/utils/dateHelpers';
import {
  BOOK_NOW_DEPARTURE_TIME_FORMAT,
  CAPTAIN_OPTION_1_INCLUDED_IN_PRICE,
  CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
} from 'src/common/constants';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import { Currency } from 'src/common/types/common/Currency';
import {
  compress, decompress,
} from 'lz-string';

export const noop = (): void => { };

type Option = {
  id?: string | number;
  name?: string;
  code?: string;
};

export const toAutocompleteOption = <T extends Option>(
  option: T,
): AutocompleteOption<T> => ({
    id: option.id ?? option.name ?? option.code,
    label: option.name ?? option.code,
    option,
  });

export default { noop };

/**
 * Will override the provided date with the new time
 */
export const timeStringToDateUpdate = (
  time: string,
  date: Date = todayDate(),
): Date => {
  const [hh, mm] = time.split(':').map((t) => parseInt(t, 10));
  date.setHours(hh, mm);
  return date;
};

/**
 * Safe timeString. Don't allow the date to be overridden
 */
export const timeStringToDate = (
  time: string,
  date: Date = todayDate(),
): Date => timeStringToDateUpdate(time, new Date(date));

/**
 * Time format is 24H clock with seconds i.e. HH:MM:SS
 */
export const tripTimeAsSeconds = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':');

  if (!hours?.length || !minutes?.length || !seconds?.length) return 0;

  return (Number(hours)) * 60 * 60 + Number(minutes) * 60 + Number(seconds);
};

export const sortInstabookTripTimes = (a: InstabookTripTime, b: InstabookTripTime): number => (
  tripTimeAsSeconds(a.min_start_time) - tripTimeAsSeconds(b.min_start_time)
);

export const getTimeString = (time: string | undefined, timeFormat: TimeFormat): string => {
  if (!time) return '';
  const date = new Date();
  const [hh, mm] = time.split(/[^0-9]/);
  return formatDate(
    set(date, { hours: Number(hh), minutes: Number(mm) }),
    timeFormat,
  );
};

export const getBookNowDepartureCopy = (tripTimes: InstabookTripTime[], t: TFunction): string => {
  if (!tripTimes?.length) return '';

  const isFixed = tripTimes[0].min_start_time === tripTimes[0].max_start_time;
  if (isFixed && tripTimes.length === 1) {
    return t('book-now-option--one-departure', {
      time: getTimeString(tripTimes[0].max_start_time, BOOK_NOW_DEPARTURE_TIME_FORMAT),
    });
  }

  const hasFlexibleTimes = tripTimes.some(
    ({ min_start_time, max_start_time }) => (min_start_time !== max_start_time),
  );

  if (!hasFlexibleTimes) {
    const copy = tripTimes
      .slice()
      .sort(sortInstabookTripTimes)
      .map((time) => formatDate(
        timeStringToDateUpdate(time.min_start_time),
        BOOK_NOW_DEPARTURE_TIME_FORMAT,
      ))
      .join(', ');

    return t('book-now-option--fixed-departure', { times: copy });
  }

  const flexibleTimesCopy = tripTimes.map(({ min_start_time, max_start_time }) => {
    if (min_start_time === max_start_time) {
      return getTimeString(min_start_time, BOOK_NOW_DEPARTURE_TIME_FORMAT);
    }
    return ` ${getTimeString(min_start_time, BOOK_NOW_DEPARTURE_TIME_FORMAT)} - ${getTimeString(max_start_time, BOOK_NOW_DEPARTURE_TIME_FORMAT)}`;
  });

  return t('book-now-option--flexible-departure', { times: flexibleTimesCopy.join(', ') });
};

export const getTripHour = (duration: string): number => {
  if (!duration) {
    return 0;
  }
  // This could be more defensive,
  // it assumes trip_length is hh:mm:ss format
  // and hours are the only digit used
  const [hh] = duration.split(':');
  return parseInt(hh, 10);
};

export const getTimeSplit = (time: string): number[] => [Number(time.split(':')[0]), Number(time.split(':')[1])];

/**
 * Converts a `trip_length` string to a duration string format.
 *
 * @param tripTime
 * Trip length as a string. `dd hh:mm:ss` for days, `hh:mm:ss` for hours.
 *
 * @returns
 * A formatted duration string. `PT${days * 24}H` for days, `PT${hours}H` for hours.
 *
 * @example
 * convertTripTimeToDuration("2 00:00:00"); // "PT48H"
 * convertTripTimeToDuration("2:00:00"); // "PT2H"
 */
export const convertTripTimeToDuration = (
  tripTime: string,
): string => {
  const parts = tripTime.split(' ');
  const multiDay = parts.length === 2;
  const hours = getTripHour(tripTime);
  return formatDuration(hours, { multiDay });
};

/** Converts <input type="time"> to locale time string
 * Input format: `13:40`
 * Output format: `1:40 PM`
*/
export const formatTimeLocal = (time: string): string => {
  const t = getTimeSplit(time);
  const date = new Date();
  date.setHours(t[0]);
  date.setMinutes(t[1]);
  // undefined uses the browser's default locale
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true }).toLocaleUpperCase();
};

export const maskPhoneNumber = (phoneNumber?: string): string => {
  if (!phoneNumber) return '';
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
  const maskedNumber = formattedPhoneNumber
    .slice(0, -4)
    .replaceAll(/\d/g, '*') + formattedPhoneNumber.slice(-4);
  return maskedNumber;
};

/**
 *
 * @param option: captain option value.
 * @returns key for captain option that can be used for translations (common.json)
 *
 * This function implements the new way of showing captain options i.e.
 * just show `With Captain` or `Self Captain`.
 */
export const getCaptainCopyText = (option: CaptainOptionValue): string => {
  if (option === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
    || option === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN) {
    return 'trip-details--with-captain';
  }
  return 'trip-details--self-captain';
};
/**
 *
 * @param option: captain option value.
 * @param only: only this option is available
 * @returns key for captain option that can be used for translations (listing-page.json)
 *
 * This function implements the new way of showing captain options i.e.
 * just show `With Captain` or `Self Captain` (or `With Captain Only`, `Self-Captain Only`).
 *
 */
export const getCaptainCopyListing = (
  option: CaptainOptionValue,
  only: boolean = false,
): string => {
  if (option === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
    || option === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN) {
    return only
      ? 'trip-cart--captain-option--with-captain-only'
      : 'trip-cart--captain-option--with-captain';
  }
  return only
    ? 'trip-cart--captain-option--self-captain-only'
    : 'trip-cart--captain-option--self-captain';
};
/**
 * Before user selects all required form values to derive a single instabook,
 * We show blank price_seciton placeholders, like `USD $--.--` (in renter currency)
 * This is preferred to loading Skeletons, which imply pending network activity.
 *
 * For the cancellation policy:
 * It tries to use the translated cancellation policy on the instabook,
 * but will fallback to re-constructing it from a listing.
 */
export const defaultAmountsPlaceholders = (
  currency: Currency,
  listing: FullBoatSearchDetail,
  t: TFunction<'price-placeholders'>,
  amountToPickCancellationPolicyFrom?: Amounts,
): Pick<Amounts, 'price_sections' | 'cancellation_policy' | 'renter_currency'> => {
  const blankPrice = `${currency.symbol}--.--`;

  const listingPolicy = t('placeholder-policy', {
    percent: listing.cancellation_refund,
    days: listing.cancellation_anticipation,
  });

  const transactionPolicy = amountToPickCancellationPolicyFrom?.[0]?.transaction
    .amounts.cancellation_policy;

  /** The policy is defined via Admin Listing Editor, and should be the same for all instabooks */
  const cancellationPolicy = transactionPolicy ?? listingPolicy;

  return {
    renter_currency: currency,
    price_sections: [
      {
        id: 'renter-trip',
        header_label: t('placeholder-your-total'),
        footer_label: t('placeholder-total'),
        formatted_amount: `${currency.code} ${blankPrice}`,
        price_groups: [
          {
            id: 'renter-fare-payment',
            label: t('placeholder-your-total'),
            formatted_amount: `${currency.code} ${blankPrice}`,
            price_lines: [
              {
                id: 'renter-fare-payment-rental-subtotal',
                label: t('placeholder-base-cost'),
                help: t('placeholder-base-cost-help'),
                formatted_amount: blankPrice,
              },
              {
                id: 'renter-fare-payment-captain-cost-subtotal',
                label: t('placeholder-captain-cost'),
                help: t('placeholder-captain-cost-help'),
                formatted_amount: blankPrice,
              },
              {
                id: 'renter-fare-payment-fees',
                label: t('placeholder-service-fee'),
                help: t('placeholder-service-fee-help'),
                formatted_amount: blankPrice,
              },
            ],
          },
        ],
      },
    ],
    cancellation_policy: cancellationPolicy,
  };
};

/**
 *
 * @param departTime format: HH:mm:ss
 * @param duration should be number.
 * @returns time return with format: HH:mm:ss
 */
export const getReturnTime = (departTime: string, duration: number): string => {
  if (Number.isNaN(duration) || duration < 0) {
    return '';
  }

  return formatDate(
    addHours(timeStringToDateUpdate(departTime), duration),
    BOOK_NOW_PICK_UP_TIME_FORMAT,
  );
};

/**
 *
 * @param time format: HH:mm:ss
 * @param duration should be number. By default it's 0;
 * @returns time return with format: HH:mm AM/PM
 */
export const getFormattedTime = (
  time: string,
  locale: Locale,
  duration: number = 0,
): string => formatDate(
  addHours(timeStringToDateUpdate(time), duration),
  BOOK_NOW_DEPARTURE_TIME_FORMAT,
  locale,
);

export const validateTripCartData = (
  data: MinimalRecentInquiry,
  listingCaptainOptions: CaptainOptionValue[],
  listingCapacity: number,
): boolean => {
  const {
    capacity, captainOptionValue, dates, duration,
  } = data;

  const [startDate, endDate] = dates;
  if (!startDate || !isDateValid(startDate)) {
    return false;
  }

  // End date should only validated when it is available.
  if (endDate && !isDateValid(endDate)) {
    return false;
  }

  if (!captainOptionValue
    || !listingCaptainOptions?.length
    || !listingCaptainOptions.includes(captainOptionValue)) {
    return false;
  }

  if (
    Number.isNaN(capacity)
    || (capacity < 1)
    || !listingCapacity
    || Number(capacity) > listingCapacity
  ) {
    return false;
  }

  const durationValue = getDurationHours(duration);
  if (!durationValue
    || Number.isNaN(durationValue)
    || !Number.isInteger(durationValue)) {
    return false;
  }
  return true;
};

/**
 * Extracts the location slug from the last breadcrumb of a listing
 * @param breadcrumbs - The array of breadcrumbs
 * @returns The location slug from the last breadcrumb, or undefined if no breadcrumbs exist
 */
export const getListingLocationSlug = (
  breadcrumbs: Breadcrumb[],
): string | undefined => breadcrumbs?.[breadcrumbs.length - 1]?.slug;

/**
 * Compresses the text using lz-string library to reduce the size of the data.
 * Input is compressed into UTF-16 format as firefox does not support UTF-8 when
 * storing in sessionStorage/localstorage.
 * Reference: https://pieroxy.net/blog/pages/lz-string/guide.html#inline_menu_1:~:text=Sample%20is%3A%20%22%20%2B%20string)%3B-,Storing%20compressed%20data%20into%20localStorage,-On%20Firefox%20and
 * @param text - The text to compress
 * @returns The compressed text
 */
export const compressText = (text: string): string => {
  if (!text) return '';

  return compress(text);
};

/**
 * Decompresses the text using lz-string library.
 * @param utf16Compressed - The compressed text in UTF-16 format
 * @returns The decompressed text
 */
export const decompressText = (utf16Compressed: string): string => {
  if (!utf16Compressed) return '';

  return decompress(utf16Compressed);
};

export type AiSummaryDisplayData = {
  hasSummary: boolean;
  showAiSummary: boolean;
};

export const getAiSummaryDisplayData = (
  aiSummary?: AISummary,
): AiSummaryDisplayData => {
  const summaryText = [
    aiSummary?.boat_info,
    aiSummary?.owner_info,
    aiSummary?.experience_info,
  ].filter(Boolean).join(' ');
  const tags = aiSummary?.great_for || [];

  return {
    hasSummary: summaryText.length > 0,
    showAiSummary: !!aiSummary && (summaryText.length > 0 || tags.length > 0),
  };
};
